import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

// Standalone public page — no login, no DashboardLayout/Sidebar (those require
// an authenticated session). Mirrors SharedLoanRekap.jsx deliberately: the two
// rekap are the same thing to whoever receives the link, so they should not
// look like two different apps.
function dueBadge(row) {
  if (row.is_overdue) {
    return <span className="badge badge-danger">Terlambat {Math.abs(row.days_until_due)} hari</span>;
  }
  if (row.days_until_due === null || row.days_until_due === undefined) {
    return <span className="badge badge-info">Tanpa tenggat</span>;
  }
  if (row.days_until_due === 0) return <span className="badge badge-danger">Jatuh tempo hari ini</span>;
  if (row.days_until_due === 1) return <span className="badge badge-warning">Jatuh tempo besok</span>;
  return <span className="badge badge-info">{row.days_until_due} hari lagi</span>;
}

export default function SharedOutgoingRekap() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Link tidak valid.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/api/transactions/shared/?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || "Link tidak valid.");
        }
        return r.json();
      })
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Link sudah kedaluwarsa atau tidak valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const totalRemaining = rows.reduce((sum, r) => sum + (r.remaining || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-semibold text-strong mb-1">Barang Belum Kembali</h1>
        <p className="text-sm text-muted mb-6">
          Rekap barang yang sudah keluar dari gudang dan belum dikembalikan.
        </p>

        {loading && <p className="text-sm text-muted">Memuat…</p>}
        {!loading && error && <div className="glass-card p-4 text-sm text-red-500">{error}</div>}

        {!loading && !error && rows.length === 0 && (
          <div className="glass-card p-4 text-sm text-muted">
            Tidak ada barang yang belum kembali. 🎉
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div className="glass-card p-4 mb-4 text-sm text-strong">
              <span className="font-semibold">{rows.length}</span> baris ·{" "}
              <span className="font-semibold">{totalRemaining}</span> barang belum kembali
            </div>

            <div className="glass-table-wrap overflow-x-auto">
              <table className="glass-table">
                <thead>
                  <tr>
                    <th className="text-left">Barang</th>
                    <th className="!text-right">Belum kembali</th>
                    <th className="text-left">Atas Nama</th>
                    <th className="text-left">Kelompok</th>
                    <th className="text-left">Pengaju</th>
                    <th className="text-left">Gudang</th>
                    <th className="text-left">Jatuh Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-2">
                        <span className="font-mono text-brand-600 dark:text-brand-400 text-xs mr-1">
                          {row.item_code}
                        </span>
                        {row.item_name}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">
                        {row.remaining}
                        {row.remaining !== row.quantity && (
                          <span className="text-xs text-muted font-normal"> / {row.quantity}</span>
                        )}{" "}
                        {row.item_unit}
                      </td>
                      <td className="px-4 py-2">{row.assign_name || row.assign_username || "-"}</td>
                      <td className="px-4 py-2 text-muted">{row.requester_team_name || "-"}</td>
                      <td className="px-4 py-2 text-muted">{row.requested_by || "-"}</td>
                      <td className="px-4 py-2 text-muted">{row.location_name}</td>
                      <td className="px-4 py-2">
                        <div>{row.due_date || "-"}</div>
                        <div className="mt-1">{dueBadge(row)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
